'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { useI18n } from '@/lib/i18n'
import { gameQueries, type BlockedKeywordField } from '@/lib/queries'
import { Loader2, Plus, ShieldBan, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

type FieldType = BlockedKeywordField

export function BlockListSection() {
  const { t } = useI18n()
  const [data, setData] = useState<Record<FieldType, string[]>>({
    name: [],
    email: [],
    displayName: [],
    speech: [],
  })
  const [newKeyword, setNewKeyword] = useState<Record<FieldType, string>>({
    name: '',
    email: '',
    displayName: '',
    speech: '',
  })
  const [loading, setLoading] = useState(true)
  const [adding, setAdding] = useState<FieldType | null>(null)
  const [removing, setRemoving] = useState<string | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)

  const loadKeywords = useCallback(async () => {
    setLoading(true)
    try {
      const result = await gameQueries.getBlockedKeywords()
      if (result.success && result.data)
        setData(result.data)
    }
    catch (error) {
      console.error('Failed to load blocked keywords:', error)
      setMessage({ type: 'error', text: 'Failed to load block list' })
    }
    finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadKeywords()
  }, [loadKeywords])

  const handleAdd = async (field: FieldType) => {
    const trimmed = newKeyword[field].trim()
    if (!trimmed)
      return
    setAdding(field)
    setMessage(null)
    try {
      await gameQueries.addBlockedKeyword(trimmed, field)
      setNewKeyword(prev => ({ ...prev, [field]: '' }))
      await loadKeywords()
      setMessage({ type: 'success', text: `"${trimmed}" ${t('admin.blockList.added', 'added')}` })
    }
    catch (error) {
      console.error('Failed to add keyword:', error)
      setMessage({ type: 'error', text: t('admin.blockList.addError', 'Failed to add keyword') })
    }
    finally {
      setAdding(null)
    }
  }

  const handleRemove = async (keyword: string, field: FieldType) => {
    setRemoving(keyword)
    setMessage(null)
    try {
      await gameQueries.removeBlockedKeyword(keyword, field)
      await loadKeywords()
      setMessage({ type: 'success', text: `"${keyword}" ${t('admin.blockList.removed', 'removed')}` })
    }
    catch (error) {
      console.error('Failed to remove keyword:', error)
      setMessage({ type: 'error', text: t('admin.blockList.removeError', 'Failed to remove keyword') })
    }
    finally {
      setRemoving(null)
    }
  }

  const fieldLabels: Record<FieldType, string> = {
    name: t('admin.blockList.fieldName', 'Name'),
    email: t('admin.blockList.fieldEmail', 'Email'),
    displayName: t('admin.blockList.fieldDisplayName', 'Display Name'),
    speech: t('admin.moderation.speechKeywords', 'Speech'),
  }

  const renderField = (field: FieldType) => {
    const keywords = data[field]
    const value = newKeyword[field]
    const isAdding = adding === field
    return (
      <div key={field} className="space-y-2 rounded-lg border border-border p-4">
        <h4 className="text-sm font-medium text-foreground">{fieldLabels[field]}</h4>
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder={t('admin.blockList.placeholder', 'e.g. keyword')}
            value={value}
            onChange={e => setNewKeyword(prev => ({ ...prev, [field]: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && handleAdd(field)}
            className="max-w-[200px]"
          />
          <Button
            onClick={() => handleAdd(field)}
            disabled={isAdding || !value.trim()}
            size="sm"
            className="gap-1"
          >
            {isAdding ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
            {t('admin.blockList.add', 'Add')}
          </Button>
        </div>
        {keywords.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {keywords.map(kw => (
              <li
                key={kw}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-1 text-sm"
              >
                <span>{kw}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-5 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemove(kw, field)}
                  disabled={removing === kw}
                  title={t('admin.blockList.remove', 'Remove')}
                >
                  {removing === kw ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div className="bg-slate-50 px-4 py-8 dark:bg-slate-900 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-xl font-bold text-foreground md:text-2xl lg:text-3xl">
            {t('admin.nav.blockList', 'Block List')}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('admin.blockList.description', 'Keywords in user name, display name, or email (case-insensitive). Users matching any keyword cannot start a game.')}
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldBan className="size-5" />
              {t('admin.blockList.blockedKeywords', 'Blocked keywords')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {message && (
              <p className={`text-sm ${message.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
                {message.text}
              </p>
            )}

            {loading
              ? (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="size-4 animate-spin" />
                    {t('admin.blockList.loading', 'Loading...')}
                  </div>
                )
              : (
                  <>
                    {renderField('name')}
                    {renderField('email')}
                    {renderField('displayName')}
                    {renderField('speech')}
                  </>
                )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
